import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Asking before something irreversible, in the app's own voice, without letting the page move.
//
// There are two of these now and they answer different questions.
//
// `AskFirst` is a panel on the page, and it does not block. That is right when the thing being
// decided is on screen and worth reading while deciding — the bill, the stock gate, the list of
// what is still with a client. The question sits beside its subject instead of covering it.
//
// This one blocks, and that is right for everything else: "Delete this comment?" is about the row
// that was just clicked, there is nothing to read while deciding, and a page that can shift
// underneath an open question is a page that can answer it about a different row.
//
// What it replaces is `window.confirm`, which blocks but cannot weight a word, cannot show more
// than one undifferentiated paragraph, offers an OK that says nothing about what it does, and
// looks like 1998 in the middle of an application that does not.
//
// ── Why a module-level function rather than a hook ──
//
// Forty-eight call sites, in forty-eight event handlers, in a dozen components that have no other
// reason to hold state. A hook would mean threading a provider value through every one of them to
// change a single line in each; this way the change at each site is the line itself. It is only
// ever called from an event handler, so there is no render-time ordering to get wrong, and there
// is exactly one dialog on screen at a time by construction.

let open = null;            // the live opener, registered by the host below
const queued = [];          // anything asked before the host has mounted

// Ask, and wait for the answer. Resolves true for the confirming button and false for anything
// else — cancel, Escape, or clicking away — so `if (!await askConfirm(…)) return;` reads the same
// way the window.confirm it replaces did.
export function askConfirm(opts) {
  const o = typeof opts === 'string' ? { body: opts } : (opts || {});
  return new Promise((resolve) => {
    if (open) open(o, resolve);
    else queued.push([o, resolve]);
  });
}

// Mounted once, near the root. Everything else talks to it through askConfirm.
export function ConfirmHost() {
  const [ask, setAsk] = useState(null);
  const resolver = useRef(null);
  const confirmRef = useRef(null);

  const answer = useCallback((yes) => {
    const r = resolver.current;
    resolver.current = null;
    setAsk(null);
    if (r) r(!!yes);
  }, []);

  useEffect(() => {
    open = (o, resolve) => { resolver.current = resolve; setAsk(o); };
    while (queued.length) { const [o, resolve] = queued.shift(); open(o, resolve); }
    return () => { open = null; };
  }, []);

  // Escape answers no, and the page underneath does not scroll while a question is open.
  useEffect(() => {
    if (!ask) return undefined;
    // `dismissValue`: what Escape or clicking away answers. False unless the question has no "cancel" —
    // "Apply to future periods too?" is two answers, and walking away from it means the default one.
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); answer(ask.dismissValue ?? false); } };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // The confirming button takes focus, so Enter answers the question rather than re-firing
    // whatever was focused behind it.
    const t = setTimeout(() => { if (confirmRef.current) confirmRef.current.focus(); }, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [ask, answer]);

  if (!ask) return null;

  const body = Array.isArray(ask.body) ? ask.body : String(ask.body || '').split('\n\n');
  const tone = ask.tone === 'plain' ? 'plain' : 'danger';

  return createPortal(
    <div className="confirm-overlay" onClick={() => answer(ask.dismissValue ?? false)}>
      <div className={`confirm-box ${tone}`} role="alertdialog" aria-modal="true"
        aria-label={ask.title || body[0] || 'Confirm'} onClick={e => e.stopPropagation()}>
        {ask.title && <div className="confirm-title">{ask.title}</div>}
        <div className="confirm-body">
          {body.filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </div>
        <div className="confirm-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => answer(false)}>
            {ask.cancelLabel || 'Cancel'}
          </button>
          {/* The verb, not "OK". What the button does should be readable without reading back
              up to the question. */}
          <button ref={confirmRef} className={`btn btn-sm ${tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => answer(true)}>
            {ask.label || 'Yes, do it'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default askConfirm;
